import React, { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, TextField } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useFormik } from "formik";
import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import {
  showToast,
  TOAST_TYPE,
  FORM_TOAST_CONTAINER_ID,
} from "@/components/Common/Toast";
import Link from "next/link";
import Image from "next/image";
import { submitSampleRequest } from "@/api/FormsApi";
import { globalSearch } from "@/api/GlobalSearchApi";
import { getCDNImageUrl, sendEventToGTM } from "@/utils/Common";
import { useRecaptcha } from "@/contexts/RecaptchaContext";
import styles from "./SampleRequestModal.module.scss";

/**
 * App-wide form-toast container id. The single `<Toaster>` for it lives in
 * `components/Layout.jsx` — see `components/Common/Toast/constants.js` for why it
 * is mounted there and not per-form.
 */
const TOAST_CONTAINER_ID = FORM_TOAST_CONTAINER_ID;

// Format brand name with ™ after the first part (e.g., "ELSURF-AOS38" → "ELSURF™-AOS38")
const formatBrandWithTrademark = (brand) => {
  if (!brand) return "";
  const mainTradeName = brand.split("-")[0] || "";
  const productName = brand.split("-").slice(1).join("-") || "";
  if (productName) {
    return `${mainTradeName}™-${productName}`;
  }
  return `${mainTradeName}™`;
};

// ProductRow: manages its own autocomplete state per row
function ProductRow({ index, row, canRemove, onChange, onRemove, autoFocus, errors = {} }) {
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchTimeout = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults([]);
      setDropdownOpen(false);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        const data = await globalSearch(q);
        setResults(Array.isArray(data) ? data : []);
        setDropdownOpen(true);
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(index, "productName", val);
    // Free-typed text may no longer match the previously selected product.
    onChange(index, "productId", null);
    setHighlightedIndex(-1);
    search(val);
  };

  const handleSelect = (product) => {
    const name = product.ChemicalName;
    onChange(index, "productName", name);
    onChange(index, "productId", product.id);
    setResults([]);
    setDropdownOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!dropdownOpen || results.length === 0) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && results[highlightedIndex]) {
          handleSelect(results[highlightedIndex]);
        }
        break;
      case "Escape":
        setDropdownOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleBlur = () => {
    // Delay so click on dropdown item registers first
    setTimeout(() => {
      setDropdownOpen(false);
      setHighlightedIndex(-1);
    }, 150);
  };

  return (
    <div className={styles.productRow}>
      <div className={styles.productRowFields}>
        <div className={styles.productNameCol}>
          <div className={styles.autocompleteWrapper}>
            <input
              ref={inputRef}
              type="text"
              value={row.productName}
              placeholder="Product Name*"
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onFocus={() => row.productName.length >= 2 && results.length > 0 && setDropdownOpen(true)}
              className={`${styles.productSearchInput} ${errors.productName ? styles.inputError : ""}`}
              autoComplete="off"
            />
            {isSearching && <span className={styles.searchSpinner} />}
            {dropdownOpen && (
              <div className={styles.autocompleteDropdown}>
                {results.length > 0 ? (
                  results.map((product, i) => (
                    <div
                      key={i}
                      className={`${styles.dropdownItem} ${i === highlightedIndex ? styles.dropdownItemHighlighted : ""}`}
                      onMouseDown={() => handleSelect(product)}
                    >
                      {product.ChemicalName}
                    </div>
                  ))
                ) : (
                  <div className={styles.dropdownNoResults}>No products found</div>
                )}
              </div>
            )}
          </div>
          {errors.productName && (
            <p className={styles.errorText}>{errors.productName}</p>
          )}
        </div>

        <div className={styles.quantityCol}>
          <input
            type="text"
            placeholder="Quantity"
            value={row.quantity}
            onChange={(e) => onChange(index, "quantity", e.target.value)}
            className={`${styles.quantityInput} ${errors.quantity ? styles.inputError : ""}`}
          />
          {errors.quantity && (
            <p className={styles.errorText}>{errors.quantity}</p>
          )}
        </div>
      </div>

      {canRemove && (
        <button
          type="button"
          onClick={() => onRemove(index)}
          className={styles.removeRowBtn}
          aria-label="Remove product"
        >
          ×
        </button>
      )}
    </div>
  );
}

function SampleRequestModal({ open, onClose, productName = "", brandName = "", productId = null, gtmSubmitEvent = "" }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [newRowAutoFocusIndex, setNewRowAutoFocusIndex] = useState(null);

  // CWV: reCAPTCHA (react-google-recaptcha + Google's recaptcha__en.js, ~3.5s of
  // main-thread JS) is only needed inside this modal. Load it lazily via the
  // shared RecaptchaProvider the moment the modal opens, instead of statically
  // importing it into the PDP page bundle. Keeps it off the critical path.
  const { ReCAPTCHA, loadRecaptcha, isLoaded } = useRecaptcha();
  useEffect(() => {
    if (open && !isLoaded) {
      loadRecaptcha();
    }
  }, [open, isLoaded, loadRecaptcha]);

  const formattedBrand = formatBrandWithTrademark(brandName);
  const prefilledName = brandName ? `${formattedBrand} - ${productName}` : productName;

  const buildInitialProducts = () => [{ productName: prefilledName, productId: productId, quantity: "" }];

  const [products, setProducts] = useState(buildInitialProducts);
  const [productErrors, setProductErrors] = useState([]);

  // Reinitialize first product when props change (e.g. different product page)
  useEffect(() => {
    setProducts((prev) => {
      const updated = [...prev];
      updated[0] = { ...updated[0], productName: prefilledName, productId: productId };
      return updated;
    });
  }, [prefilledName, productId]);

  const formik = useFormik({
    initialValues: {
      deliveryLocation: "",
      fullName: "",
      email: "",
      companyName: "",
      mobileNumber: "",
      recaptcha: null,
      termsAccepted: false,
    },
    validationSchema: Yup.object().shape({
      deliveryLocation: Yup.string().trim().required("Delivery location is required"),
      fullName: Yup.string().trim().required("Full name is required"),
      email: Yup.string()
        .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i, "Enter a valid email address")
        .required("Email is required"),
      companyName: Yup.string().trim().required("Company name is required"),
      mobileNumber: Yup.string(),
      recaptcha: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY
        ? Yup.string().required("Please complete the reCAPTCHA")
        : Yup.string().nullable(),
      termsAccepted: Yup.boolean().oneOf([true], "You must agree to the Terms & Conditions"),
    }),
    enableReinitialize: true,
    validate: () => {
      const errors = products.map((p) => ({
        productName: !p.productName.trim() ? "Product name is required" : "",
        quantity: "",
      }));
      setProductErrors(errors);
      // Return a dummy key so Formik blocks submission when product rows are invalid
      if (errors.some((e) => e.productName)) {
        return { _products: "invalid" };
      }
      return {};
    },
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const result = await submitSampleRequest({ ...values, products });
        if (result) {
          setIsSuccess(true);
          if (gtmSubmitEvent) {
            sendEventToGTM(gtmSubmitEvent, {
              page_url: typeof window !== "undefined" ? window.location.href : "",
            });
          }
          formik.resetForm();
          setProducts(buildInitialProducts());
          setProductErrors([]);
          window?.grecaptcha?.reset();
          // No success toast: `isSuccess` swaps the modal body for a success panel
          // that stays up 5s before auto-closing, so a toast would repeat it.
          // Failures still toast — they keep the form on screen with no panel.
        } else {
          showToast("Something went wrong, please try again!", {
            type: TOAST_TYPE.ERROR,
            containerId: TOAST_CONTAINER_ID,
          });
        }
      } catch (error) {
        console.error("Form submission error:", error);
        showToast("Something went wrong, please try again!", {
          type: TOAST_TYPE.ERROR,
          containerId: TOAST_CONTAINER_ID,
        });
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      formik.resetForm();
      setProducts(buildInitialProducts());
      setProductErrors([]);
      setIsSuccess(false);
      setNewRowAutoFocusIndex(null);
    }
  }, [open]);

  // Auto close after success
  useEffect(() => {
    if (isSuccess) {
      const timeoutId = setTimeout(() => {
        setIsSuccess(false);
        onClose();
      }, 5000);
      return () => clearTimeout(timeoutId);
    }
  }, [isSuccess, onClose]);

  const handleProductChange = (index, field, value) => {
    setProducts((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    // Clear error for the changed field - only productName/quantity carry tracked
    // errors; productId has no entry in productErrors and isn't always a string
    // (number, or null when cleared), so guard both the key and the type here.
    setProductErrors((prev) => {
      if (!prev[index] || !(field in prev[index])) return prev;
      const updated = [...prev];
      const isEmpty = typeof value === "string" ? !value.trim() : !value;
      updated[index] = { ...updated[index], [field]: isEmpty ? updated[index][field] : "" };
      return updated;
    });
  };

  const handleAddProduct = () => {
    if (products.length >= 15) return;
    const newIndex = products.length;
    setProducts((prev) => [...prev, { productName: "", productId: null, quantity: "" }]);
    setNewRowAutoFocusIndex(newIndex);
  };

  const handleRemoveProduct = (index) => {
    setProducts((prev) => prev.filter((_, i) => i !== index));
    setProductErrors((prev) => prev.filter((_, i) => i !== index));
    setNewRowAutoFocusIndex(null);
  };

  const handleClose = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            maxWidth: "520px",
          },
        }}
      >
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Request Sample</h2>
          <ClearIcon className={styles.closeIcon} onClick={handleClose} />
        </div>

        <DialogContent className={styles.modalContent}>
          {!isSuccess ? (
            <form onSubmit={formik.handleSubmit} className={styles.form}>
              {/* Sample Details Section */}
              <div className={styles.formSection}>
                <label className={styles.sectionLabel}>Sample Details</label>

                {products.map((row, index) => (
                  <div key={index} className={styles.productRowWrapper}>
                    <ProductRow
                      index={index}
                      row={row}
                      canRemove={products.length > 1}
                      onChange={handleProductChange}
                      onRemove={handleRemoveProduct}
                      autoFocus={newRowAutoFocusIndex === index}
                      errors={productErrors[index] || {}}
                    />
                  </div>
                ))}

                {products.length < 15 && (
                  <div className={styles.addMoreBtnRow}>
                    <button type="button" onClick={handleAddProduct} className={styles.addMoreBtn}>
                      <span className={styles.addMorePlus}>+</span> Add Product
                    </button>
                  </div>
                )}

                <TextField
                  fullWidth
                  name="deliveryLocation"
                  placeholder="Delivery Location*"
                  value={formik.values.deliveryLocation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.deliveryLocation && Boolean(formik.errors.deliveryLocation)}
                  helperText={formik.touched.deliveryLocation && formik.errors.deliveryLocation}
                  sx={inputStyles}
                />
              </div>

              {/* Contact Details Section */}
              <div className={styles.formSection}>
                <label className={styles.sectionLabel}>Contact Details</label>

                <TextField
                  fullWidth
                  name="fullName"
                  placeholder="Full Name*"
                  value={formik.values.fullName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.fullName && Boolean(formik.errors.fullName)}
                  helperText={formik.touched.fullName && formik.errors.fullName}
                  sx={inputStyles}
                />

                <TextField
                  fullWidth
                  name="email"
                  type="email"
                  placeholder="Email ID*"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  sx={inputStyles}
                />

                <TextField
                  fullWidth
                  name="companyName"
                  placeholder="Company Name*"
                  value={formik.values.companyName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.companyName && Boolean(formik.errors.companyName)}
                  helperText={formik.touched.companyName && formik.errors.companyName}
                  sx={inputStyles}
                />

                <div className={styles.phoneInputWrapper}>
                  <PhoneInput
                    country={"us"}
                    enableSearch={true}
                    value={formik.values.mobileNumber}
                    onChange={(value) => formik.setFieldValue("mobileNumber", value)}
                    onBlur={() => formik.handleBlur("mobileNumber")}
                    placeholder="Mobile Number"
                    inputStyle={{
                      width: "100%",
                      height: "48px",
                      fontSize: "16px",
                      fontFamily: "var(--font-mukta), sans-serif",
                      backgroundColor: "#fff",
                      border: "1px solid #E0E0E0",
                      borderRadius: "0 8px 8px 0",
                      borderLeft: "none",
                      paddingLeft: "68px",
                    }}
                    buttonStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #E0E0E0",
                      borderRadius: "8px 0 0 8px",
                      borderRight: "none",
                    }}
                    dropdownStyle={{
                      borderRadius: "8px",
                      zIndex: 10,
                      maxHeight: "200px",
                    }}
                  />
                  {formik.touched.mobileNumber && formik.errors.mobileNumber && (
                    <p className={styles.errorText}>{formik.errors.mobileNumber}</p>
                  )}
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className={styles.termsContainer}>
                <input
                  type="checkbox"
                  id="termsAccepted"
                  name="termsAccepted"
                  checked={formik.values.termsAccepted}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={styles.checkbox}
                />
                <label htmlFor="termsAccepted" className={styles.termsLabel}>
                  By submitting, I agree with{" "}
                  <Link href="/terms-and-conditions" className={styles.termsLink} target="_blank">
                    Terms & Conditions
                  </Link>
                </label>
              </div>
              {formik.touched.termsAccepted && formik.errors.termsAccepted && (
                <p className={styles.errorText}>{formik.errors.termsAccepted}</p>
              )}

              {/* reCAPTCHA */}
              {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && ReCAPTCHA && (
                <div className={styles.recaptchaContainer}>
                  <ReCAPTCHA
                    sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                    onChange={(value) => formik.setFieldValue("recaptcha", value)}
                  />
                  {formik.touched.recaptcha && formik.errors.recaptcha && (
                    <p className={styles.errorText}>{formik.errors.recaptcha}</p>
                  )}
                </div>
              )}

              <button type="submit" disabled={isLoading} className={styles.submitButton}>
                {isLoading ? "Submitting..." : "Submit"}
              </button>
            </form>
          ) : (
            <div className={styles.successContainer}>
              <div className={styles.successIcon}>
                <Image
                  src={getCDNImageUrl("solidGreenContactUs.svg")}
                  alt="Success"
                  width={120}
                  height={120}
                />
              </div>
              <p className={styles.successText}>
                Your sample request has been sent successfully! We will get back to you shortly.
              </p>
              <button type="button" onClick={handleClose} className={styles.submitButton}>
                Close
              </button>
            </div>
          )}
          </DialogContent>
      </Dialog>
    </>
  );
}

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "#E0E0E0" },
    "&:hover fieldset": { borderColor: "#B3B3B3" },
    "&.Mui-focused fieldset": { borderColor: "#EF4136" },
  },
  "& .MuiOutlinedInput-input": {
    padding: "12px 14px",
    fontSize: "16px",
    fontFamily: "var(--font-mukta), sans-serif",
    "&::placeholder": { color: "#808080", opacity: 1 },
  },
  "& .MuiFormHelperText-root": { marginLeft: 0, marginTop: "4px" },
  marginBottom: "12px",
};

export default SampleRequestModal;
